/**
 * Socket.io widget transport — Phase 1B Slice V-1.
 *
 * Implements {@link WidgetTransport} on top of `socket.io-client`. The
 * gateway runs a regex namespace match for `/widget` and `/dashboard`
 * (see `apps/api/src/realtime/realtime.gateway.ts`). The widget
 * connects to `/widget` with the JWT issued by `POST /v1/widget/session`
 * passed as `auth.token`.
 *
 * Design notes:
 *   - The `createSocket` factory is injectable so tests can substitute
 *     a fake without touching the network. Production callers receive
 *     the real `io()` from `socket.io-client`.
 *   - State transitions follow the socket.io lifecycle: `connect`,
 *     `disconnect`, `connect_error`. We map these to the four
 *     transport states the client expects.
 *   - `emit()` is fire-and-forget; `emitWithAck()` uses socket.io's ack
 *     callback (the @SubscribeMessage handler's return value) so callers
 *     can detect rejected/lost sends instead of assuming success.
 */

import { io, type Socket } from 'socket.io-client'
import type { WidgetTransport } from './client'

export type SocketFactory = (
  socketUrl: string,
  opts: { auth: { token: string }; transports?: string[] },
) => Socket

/**
 * Default factory — wraps `socket.io-client`'s `io()`. Exposed as a
 * named export so production `main.ts` can reuse it and tests can
 * swap it out.
 */
export const defaultSocketFactory: SocketFactory = (socketUrl, opts) =>
  io(socketUrl, opts)

function buildWidgetSocketUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl.replace(/\/$/, '')}/widget`
}

export interface SocketIoTransportOptions {
  apiBaseUrl: string
  /** Inject for tests; defaults to `defaultSocketFactory`. */
  createSocket?: SocketFactory
  /** Override the polling→websocket transports list. Default: ['websocket','polling']. */
  transports?: string[]
  /** Default emitWithAck timeout. Default: 10s. */
  ackTimeoutMs?: number
}

const DEFAULT_ACK_TIMEOUT_MS = 10_000

export function createSocketIoTransport(
  opts: SocketIoTransportOptions,
): WidgetTransport {
  const createSocket = opts.createSocket ?? defaultSocketFactory
  const transports = opts.transports ?? ['websocket', 'polling']

  let socket: Socket | null = null
  const stateHandlers = new Set<
    (s: 'connecting' | 'connected' | 'disconnected' | 'error') => void
  >()
  const eventHandlers = new Map<string, Set<(p: unknown) => void>>()

  function notifyState(state: 'connecting' | 'connected' | 'disconnected' | 'error') {
    for (const h of stateHandlers) h(state)
  }
  function notifyEvent(event: string, payload: unknown) {
    const set = eventHandlers.get(event)
    if (!set) return
    for (const h of set) h(payload)
  }

  return {
    async connect({ token }) {
      // Tear down any previous socket before reconnecting (e.g. token refresh).
      if (socket) {
        socket.removeAllListeners()
        socket.disconnect()
        socket = null
      }
      notifyState('connecting')
      socket = createSocket(buildWidgetSocketUrl(opts.apiBaseUrl), {
        auth: { token },
        transports,
      })
      // socket.io fires `connect` on successful handshake. We treat
      // that as 'connected' for the client state machine.
      socket.on('connect', () => notifyState('connected'))
      socket.on('disconnect', () => notifyState('disconnected'))
      socket.on('connect_error', () => notifyState('error'))
      // Forward server-pushed events through the generic `on(event)`
      // channel. The widget client subscribes to `message:new` and `typing`.
      socket.onAny((event, ...args) => {
        // server emits one payload argument — pass it through verbatim.
        notifyEvent(event, args[0])
      })
    },

    disconnect() {
      if (!socket) return
      socket.removeAllListeners()
      socket.disconnect()
      socket = null
    },

    emit(event, payload) {
      if (!socket) return
      socket.emit(event, payload)
    },

    emitWithAck(event, payload, timeoutMs) {
      if (!socket) {
        return Promise.reject(new Error('not-connected'))
      }
      const timeout = timeoutMs ?? opts.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS
      const s = socket
      // socket.io's timeout() wrapper invokes the ack callback with
      // (err) on timeout and (null, response) on success.
      return new Promise((resolve, reject) => {
        s.timeout(timeout).emit(event, payload, (err: unknown, response: unknown) => {
          if (err) reject(new Error('ack-timeout'))
          else resolve(response)
        })
      })
    },

    on(event, handler) {
      let set = eventHandlers.get(event)
      if (!set) {
        set = new Set()
        eventHandlers.set(event, set)
      }
      set.add(handler)
    },

    off(event, handler) {
      eventHandlers.get(event)?.delete(handler)
    },

    onState(handler) {
      stateHandlers.add(handler)
    },
  }
}
